import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
// import uuid from 'uuid/v4';
import * as d3 from 'd3';
import _ from 'lodash';
import classNames from 'classnames';
import { CAC_CLUST_MODEL_PERFORMANCE_BODY } from '../CacConstants';
import useResizeObserver from '../chart/useResizeObserver';
import ClusterViewSelector from './ClusterViewSelector';

// segment and cluster are the same thing
const ClusterModelPerfBlock = props => {
  const { project, selectedCluster, setSelectedCluster, projectData, clustersVisible,  setClustersVisible } = props;
  const [prevSelectedCluster, setPrevSelectedCluster] = useState(-1);
  const [prevOptimalVal, setPrevOptimalVal] = useState(-1);

  const solutions = projectData.solutions ? projectData.solutions : [];
  const wrapperRef = useRef();
  const svgRef = useRef();
  const resizeObserver = useResizeObserver(wrapperRef);

  const clusterVariance =
    selectedCluster >= 0 && solutions.length > 0 ? solutions[selectedCluster].variance : 0;
  const numberOfSegments = cluster =>
    cluster >= 0 && solutions.length > 0 ? solutions[cluster].segmentCount : 0;
  const minSegment =
    selectedCluster >= 0 && solutions.length > 0
      ? _.minBy(solutions[selectedCluster].segments, s => s.pct).pct
      : 0;
  const maxSegment =
    selectedCluster >= 0 && solutions.length > 0
      ? _.maxBy(solutions[selectedCluster].segments, s => s.pct).pct
      : 0;

  const mostOptimal = _.findIndex(solutions, o => o.optimal === true);
  const optimalVal = selectedCluster >= 0 && solutions.length > 0 ? solutions[mostOptimal].variance : 0;
  const mr = val => Math.round(val);

  const buildChart = () => {
    if (!resizeObserver || !resizeObserver.width || resizeObserver.height === 0)
      return;

    const chartData = solutions.map(x => x.variance);
    const bandScale = d3
      .scaleBand()
      .domain(chartData)
      .range([0, resizeObserver.width])
      .padding(0.3);

    const yScale = d3
      .scaleLinear()
      .domain([0, d3.max(chartData)])
      .rangeRound([0, resizeObserver.height]);

    const barWidth = bandScale.bandwidth();
    const height = resizeObserver.height;

    const svg = d3.select(svgRef.current);
    svg
      .attr('background', 'white')
      .style('overflow', 'visible')
      .attr('width', resizeObserver.width)
      .attr('height', resizeObserver.height);

    const barchart = svg.select('.barchart');

    const linechart = svg.select('.linechart');
    linechart.selectAll('.clusterLine').remove();

    // https://medium.com/welldone-software/tooltips-using-svg-path-1bd69cc7becd
    const topTooltipPath = (width, height, offset, radius) => {
      const left = -width / 2;
      const right = width / 2;
      const top = -offset - height;
      const bottom = -offset;
      return (
        `M 0,0 L ${-offset},${bottom} H ${left + radius} Q ${left},${bottom}` +
        ` ${left},${bottom - radius} V ${top + radius}` +
        ` Q ${left},${top} ${left + radius},${top} H ${right - radius}` +
        ` Q ${right},${top} ${right},${top + radius} V ${bottom - radius}` +
        ` Q ${right},${bottom} ${right - radius},${bottom} H ${offset} L 0,0 z`
      );
    };

    const showTooltipTop = (obj, x, y) => {
      if (obj) {
        const container = svg.select('.hoverTT');
        container.attr('transform', `translate(${x},${y})`);

        d3.select('.ttPath').attr('d', topTooltipPath(20, 10, 4, 4));
        d3.select('.ttText').text(mr(obj).toString());

        container.attr('opacity', 1);
      } else {
        d3.select('.hoverTT').attr('opacity', 0);
      }
    };

    const pointArray = chartData.map(d => {
      return {
        x: bandScale(d),
        y: height - yScale(d),
      };
    });

    barchart
      .selectAll('.clusterBar')
      .data(chartData, d => d)
      .join(
        enter => enter.append('rect'),
        update => update.attr('class', 'clusterBar'),
        exit => exit.remove()
      )
      .attr('class', 'clusterBar')
      .attr('id', (_, i) => `ClusterBar_${i}`)
      .style('fill', d => (d === optimalVal ? '#37BB5C' : '#C4C4C4'))
      .attr('width', barWidth)
      .attr('height', d => yScale(d))
      .attr('x', (_, i) => pointArray[i].x)
      .attr('y', (_, i) => pointArray[i].y)
      .attr('filter', (_, i) => {
        return selectedCluster !== i ? 'none' : 'url(#filter1)';
      })
      .on('click', (_, i) => {
        setSelectedCluster(project.projectId, i, numberOfSegments(i), null);
      })
      .on('mouseover', (d, i) => {
        showTooltipTop(d, pointArray[i].x + barWidth / 2, pointArray[i].y - 9);
      })
      .on('mouseout', () => {
        showTooltipTop(null);
      });
  };

  const ro = resizeObserver && resizeObserver.width;
  useEffect(() => {
    if (selectedCluster >= 0 && ro) buildChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ro]);

  useEffect(() => {
    if (selectedCluster === -1) {
      setSelectedCluster(
        project.projectId,
        mostOptimal,
        numberOfSegments(mostOptimal),
        null
      );
    }
    if (
      prevSelectedCluster !== selectedCluster ||
      prevOptimalVal !== optimalVal
    ) {
      buildChart();
      setPrevSelectedCluster(selectedCluster);
      setPrevOptimalVal(optimalVal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCluster, optimalVal]);

  const buildSVGSection = () => {
    return (
      <div
        className="mpcl-area"
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        <div ref={wrapperRef} className="mpcl-bar">
          <svg
            ref={svgRef}
            id="ClusterModPerf"
            data-test="ClusterModPerf"
            className="clusterD3_svgBox"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <filter id="filter1" x="0" y="0" width="120%" height="123%">
                <feOffset result="offOut" in="SourceAlpha" dx="0" dy="0" />
                <feGaussianBlur result="blurOut" in="offOut" stdDeviation="3" />
                <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
              </filter>
              <filter id="filter2">
                <feDropShadow
                  dx="0.0"
                  dy="0.0"
                  stdDeviation="2.5"
                  floodColor="lightgray"
                />
              </filter>
            </defs>
            <g className="barchart" />
            <g className="linechart" />
            <g className="hoverTT">
              <path
                className="ttPath"
                fill="white"
                stroke="black"
                strokeOpacity="0.5"
                filter="url(#filter2)"
              />
              <text
                className="ttText"
                y="-6"
                fontSize="8px"
                fontWeight="400"
                fontFamily="Open Sans"
                textAnchor="middle"
              />
            </g>
          </svg>
        </div>
        <div className="mpcl-msg">
          Select any bar above to view the performance summary
        </div>
      </div>
    );
  };

  const buildEfficiencySection = () => {
    return (
      <div className="mpcl-area">
        <div
          className={classNames(
            'mpcl-optimal-text',
            { 'mpcl-values-less': clusterVariance !== optimalVal },
            { 'mpcl-values-most': clusterVariance === optimalVal }
          )}
        >
          {`${selectedCluster === mostOptimal ? 'MOST' : 'LESS'} EFFICIENT`}
        </div>
        <div className="mpcl-subText">Cluster Solution</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto auto' }}>
          <div className="mpcl-rgBlk">
            <div className="mpcl-blkTit" id="clusterVariance">{`${mr(
              clusterVariance
            )}%`}</div>
            <div className="mpcl-blkTxt" style={{ paddingRight: '5px' }}>
              Total Variance Explained
            </div>
          </div>
          <div className="mpcl-rgBlk">
            <div className="mpcl-blkTit" id="numberOfSegments">
              {numberOfSegments(selectedCluster)}
            </div>
            <div className="mpcl-blkTxt">Segments</div>
          </div>
          <div className="mpcl-rgBlk">
            <div className="mpcl-blkTit" id="maxSegment">{`${mr(
              maxSegment
            )}%`}</div>
            <div className="mpcl-blkTxt">Largest Segment</div>
          </div>
          <div className="mpcl-rgBlk">
            <div className="mpcl-blkTit" id="minSegment">{`${mr(
              minSegment
            )}%`}</div>
            <div className="mpcl-blkTxt">Smallest Segment</div>
          </div>
        </div>
      </div>
    );
  };

  const buildHTMLBlock = () => {
    return projectData.errMsg ? (
      <div ref={wrapperRef}>
        Data does not support cluster maps.
        <svg
          ref={svgRef}
          id="ClusterModPerf"
          data-test="ClusterModPerf"
          className="clusterD3_svgBox"
          xmlns="http://www.w3.org/2000/svg"
        />
      </div>
    ) : (
      <React.Fragment>
        {buildSVGSection()}
        {buildEfficiencySection()}
      </React.Fragment>
    );
  };

  return (
    <div className="model-clust-perf-block" data-test="ClusterModelPerfBlock">
      <div className="mpcl-title">Model Performance</div>
      <p className="mpcl-text">{CAC_CLUST_MODEL_PERFORMANCE_BODY}</p>
      <div className="mpcl-body">{buildHTMLBlock()}</div>
      <div>
        <ClusterViewSelector
          clusterMapView={project.clusterMapView}
          clustersVisible={clustersVisible}
          setClustersVisible={setClustersVisible}
        />
      </div>
    </div>
  );
};

ClusterModelPerfBlock.propTypes = {
  project: PropTypes.shape({
    projectId: PropTypes.number,
    clusterMapView: PropTypes.bool,
  }),
  projectData: PropTypes.shape({
    errMsg: PropTypes.string,
    solutions: PropTypes.arrayOf(
      PropTypes.shape({
        variance: PropTypes.number,
        segmentCount: PropTypes.number,
        segments: PropTypes.arrayOf(PropTypes.shape({})),
      })
    ),
  }),
  selectedCluster: PropTypes.number,
  setSelectedCluster: PropTypes.func,
};

export default ClusterModelPerfBlock;
